import { Controller } from "@hotwired/stimulus"

// Shared accessible dropdown for the public attendee header — drives BOTH
// desktop dropdowns (the guest "Features" menu and the signed-in account menu),
// replacing the old CSS-hover Features mechanism and the Bootstrap
// data-bs-toggle account menu. Dependency-free (no Bootstrap).
//
// Markup contract:
//   <div data-controller="sg-dropdown">
//     <button data-sg-dropdown-target="trigger"
//             data-action="click->sg-dropdown#toggle"
//             aria-haspopup="menu" aria-expanded="false"
//             aria-controls="PANEL_ID"> … </button>
//     <div id="PANEL_ID" data-sg-dropdown-target="panel" role="menu">
//       <a role="menuitem" …>…</a>  (or <button role="menuitem">)
//     </div>
//   </div>
//
// Behaviour:
//   * click/focus activated (NOT hover-only); toggle on trigger click.
//   * Escape closes and returns focus to the trigger.
//   * Arrow Up/Down move focus between items (wrap at ends); Home/End jump.
//   * click-outside closes.
//
// Security: nav chrome only — never sets innerHTML; all items are static,
// server-rendered links.
export default class extends Controller {
  static targets = ["trigger", "panel"]

  connect() {
    this._onDocClick = this._onDocClick.bind(this)
    this._onDocKeydown = this._onDocKeydown.bind(this)
    this._syncAria(false)
  }

  disconnect() {
    this._teardownGlobalListeners()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (this.isOpen) return
    this.panelTarget.classList.add("is-open")
    this._syncAria(true)
    document.addEventListener("click", this._onDocClick)
    document.addEventListener("keydown", this._onDocKeydown)
  }

  close({ focusTrigger = false } = {}) {
    if (!this.isOpen) {
      if (focusTrigger) this.triggerTarget.focus()
      return
    }
    this.panelTarget.classList.remove("is-open")
    this._syncAria(false)
    this._teardownGlobalListeners()
    if (focusTrigger) this.triggerTarget.focus()
  }

  get isOpen() {
    return this.panelTarget.classList.contains("is-open")
  }

  // ── Keyboard handling on the trigger (Arrow/Enter/Space open + focus) ──
  triggerKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
      case "Down":
        event.preventDefault()
        this.open()
        this._focusItem(0)
        break
      case "ArrowUp":
      case "Up":
        event.preventDefault()
        this.open()
        this._focusItem(this._items.length - 1)
        break
      case "Enter":
      case " ":
      case "Spacebar":
        event.preventDefault()
        this.isOpen ? this.close({ focusTrigger: true }) : (this.open(), this._focusItem(0))
        break
    }
  }

  // ── Keyboard handling inside the panel (roving focus) ──
  panelKeydown(event) {
    const items = this._items
    if (items.length === 0) return
    const currentIndex = items.indexOf(document.activeElement)

    switch (event.key) {
      case "ArrowDown":
      case "Down":
        event.preventDefault()
        this._focusItem(currentIndex < 0 ? 0 : (currentIndex + 1) % items.length)
        break
      case "ArrowUp":
      case "Up":
        event.preventDefault()
        this._focusItem(currentIndex <= 0 ? items.length - 1 : currentIndex - 1)
        break
      case "Home":
        event.preventDefault()
        this._focusItem(0)
        break
      case "End":
        event.preventDefault()
        this._focusItem(items.length - 1)
        break
      case "Escape":
      case "Esc":
        event.preventDefault()
        this.close({ focusTrigger: true })
        break
      case "Tab":
        // ARIA menu pattern: Tab exits the menu. Close (no preventDefault) so the
        // browser moves focus to the next element naturally instead of trapping it.
        this.close()
        break
    }
  }

  // ── internals ──
  get _items() {
    return Array.from(
      this.panelTarget.querySelectorAll('[role="menuitem"]:not([disabled])')
    )
  }

  _focusItem(index) {
    const items = this._items
    if (items.length === 0) return
    const clamped = ((index % items.length) + items.length) % items.length
    items[clamped].focus()
  }

  _syncAria(open) {
    this.triggerTarget.setAttribute("aria-expanded", String(open))
  }

  _onDocClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  _onDocKeydown(event) {
    // Document-level Escape catches the case where focus is on the trigger
    // (panelKeydown only fires while focus is inside the panel).
    if (event.key === "Escape" || event.key === "Esc") {
      this.close({ focusTrigger: true })
    }
  }

  _teardownGlobalListeners() {
    document.removeEventListener("click", this._onDocClick)
    document.removeEventListener("keydown", this._onDocKeydown)
  }
}
